import * as Monaco from 'monaco-editor';
import {editor} from 'monaco-editor';
import {CompilerService} from './compiler/CompilerService';
import {LanguageService} from './languages/languageService';

interface EditorState {
    model: editor.ITextModel | null;
    languageService: LanguageService | null;
    compilerService: CompilerService | null;
    disposables: Monaco.IDisposable[];
}

interface LanguageDefinition {
    id: string;
    extensions: string[];
}

export class EditorModelManager {
    private monaco: typeof Monaco;
    private state: EditorState = {
        model: null,
        languageService: null,
        compilerService: null,
        disposables: []
    };
    private initialized = false;
    private styleMap = new Map<string, HTMLStyleElement>();

    constructor(monaco: typeof Monaco) {
        this.monaco = monaco;
    }

    async initialize() {
        if (this.initialized) return;

        try {
            // Verify Monaco is ready
            if (!this.monaco.editor) {
                throw new Error('Monaco editor not ready');
            }

            // Disable Monaco's built-in validators before anything else
            this.disableBuiltInValidation();

            this.registerGlobalMarkerFilter();

            // Theme is now initialized separately before this
            await this.setupEditor();
            this.initialized = true;
        } catch (error) {
            console.error('Editor initialization failed:', error);
            throw error;
        }
    }

    private disableBuiltInValidation() {
        // Disable TypeScript/JavaScript validation - this part works fine
        if (this.monaco.languages.typescript) {
            // TypeScript
            this.monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
                noSemanticValidation: true,
                noSyntaxValidation: true,
                noSuggestionDiagnostics: true
            });

            // JavaScript
            this.monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
                noSemanticValidation: true,
                noSyntaxValidation: true,
                noSuggestionDiagnostics: true
            });
        }

        // Handle HTML and CSS validation by intercepting markers instead
        // This avoids using properties that don't exist in your Monaco version

        // Create a marker filter for HTML and CSS
        const originalSetModelMarkers = this.monaco.editor.setModelMarkers;
        this.monaco.editor.setModelMarkers = (model, owner, markers) => {
            // If this is an HTML or CSS model, remove all diagnostics markers
            const languageId = model.getLanguageId();
            if (languageId === 'html' || languageId === 'css') {
                // Return empty array of markers for HTML and CSS
                return originalSetModelMarkers.call(this.monaco.editor, model, owner, []);
            }

            // For all other languages, proceed normally
            return originalSetModelMarkers.call(this.monaco.editor, model, owner, markers);
        };

        // Add to disposables to restore original function when needed
        this.state.disposables.push({
            dispose: () => {
                this.monaco.editor.setModelMarkers = originalSetModelMarkers;
            }
        });

        console.log('[DEBUG] Disabled built-in Monaco validators using marker interception');
    }

    public async initializeTheme() {
        const customTheme: Monaco.editor.IStandaloneThemeData = {
            base: 'vs',
            inherit: true,
            rules: [
                {token: 'keyword', foreground: '859900'},
                {token: 'type', foreground: '268BD2'},
                {token: 'string', foreground: '2AA198'},
                {token: 'number', foreground: 'CB4B16'},
                {token: 'comment', foreground: '93A1A1'},
                {token: 'operator', foreground: '859900'},
                {token: 'delimiter', foreground: '657B83'},
                {token: 'delimiter.bracket', foreground: '657B83'},
                {token: 'delimiter.parenthesis', foreground: '657B83'},
                {token: 'delimiter.curly', foreground: '657B83'},
                {token: 'identifier', foreground: '657B83'},
                {token: 'keyword.operator', foreground: '859900'},
                {token: 'variable', foreground: '657B83'},
                {token: 'function', foreground: '268BD2'},
                {token: 'constant', foreground: 'CB4B16'}
            ],
            colors: {
                'editor.background': '#FDF6E3',
                'editor.foreground': '#657B83',
                'editorGutter.background': '#EEE8D5',
                'editorLineNumber.foreground': '#93A1A1',
                'editorLineNumber.activeForeground': '#657B83',
                'editor.lineHighlightBackground': '#EEE8D5',
                'editor.selectionBackground': '#E4E4E4',
                'editorBracketMatch.background': '#EEE8D5',
                'editorBracketMatch.border': '#657B83',
                'editorBracketPairGuide.activeBackground1': '#657B83',
                'editorBracketPairGuide.activeBackground2': '#657B83',
                'editorBracketPairGuide.activeBackground3': '#657B83'
            }
        };

        try {
            // Define and set the theme immediately
            this.monaco.editor.defineTheme('customTheme', customTheme);
            this.monaco.editor.setTheme('customTheme');

            // Add a small delay to ensure theme is applied
            await new Promise(resolve => setTimeout(resolve, 50));

        } catch (error) {
            console.error('Failed to initialize theme:', error);
            throw error;
        }
    }


    public preloadTypeScriptFiles(files: Array<{ name: string; content: string }>) {
        if (!this.monaco.languages.typescript) return;

        const typescript = this.monaco.languages.typescript;
        const processedFiles = new Set(); // Track processed files

        files.forEach(file => {
            if (processedFiles.has(file.name)) return; // Skip if already processed

            if (file.name.endsWith('.ts') || file.name.endsWith('.tsx') ||
                file.name.endsWith('.js') || file.name.endsWith('.jsx') ||
                file.name.endsWith('.did.js') || file.name.endsWith('.did')) {

                const filePath = `file:///${file.name}`;
                typescript.typescriptDefaults.addExtraLib(
                    file.content,
                    filePath
                );
                processedFiles.add(file.name);
            }
        });
    }


    private async setupEditor() {
        // Add custom styles
        this.addCustomStyles();

        // Initialize compiler service if available
        if (this.state.compilerService) {
            await this.state.compilerService.init();
        }

        // Setup editor features
        this.setupEditorFeatures();
    }

    private setupEditorFeatures() {
        if (this.state.model) {
            const changeListener = this.state.model.onDidChangeContent(() => {
                this.handleModelChange();
            });
            this.state.disposables.push(changeListener);
        }
    }

    private async handleModelChange() {
        if (!this.state.model || !this.state.languageService) return;

        try {
            const content = this.state.model.getValue();
            const fileName = this.state.model.uri.path;
            if (!fileName) return;

            await this.state.languageService.handleDiagnostics(fileName, content);
        } catch (error) {
            console.error('Model change handling failed:', error);
        }
    }

    public registerLanguages(languages: LanguageDefinition[]): void {
        try {
            languages.forEach(lang => {
                this.monaco.languages.register({
                    id: lang.id,
                    extensions: lang.extensions
                });
            });
        } catch (error) {
            console.error('Failed to register languages:', error);
        }
    }

    public configureTypeScript(compilerOptions: Monaco.languages.typescript.CompilerOptions): void {
        try {
            if (this.monaco.languages.typescript) {
                this.monaco.languages.typescript.typescriptDefaults.setCompilerOptions(compilerOptions);
                this.monaco.languages.typescript.javascriptDefaults.setCompilerOptions(compilerOptions);
            }
        } catch (error) {
            console.error('Failed to configure TypeScript:', error);
        }
    }

    setModel(uri: string, content: string, language: string): editor.ITextModel {
        try {
            const existingModel = this.monaco.editor.getModel(this.monaco.Uri.parse(uri));
            if (existingModel) {
                if (this.state.languageService) {
                    this.state.languageService.setLanguageForModel(existingModel, language);
                }
                return existingModel;
            }

            this.disposeCurrentModel();
            const newModel = this.monaco.editor.createModel(
                content,
                language,
                this.monaco.Uri.parse(uri)
            );
            this.state.model = newModel;
            this.setupEditorFeatures();
            return newModel;
        } catch (error) {
            console.error('Model creation failed:', error);
            throw new Error('Failed to create editor model');
        }
    }

    public registerGlobalMarkerFilter() {
        const originalSetModelMarkers = this.monaco.editor.setModelMarkers;

        // Store the reference to the original function and create a new wrapped version
        this.monaco.editor.setModelMarkers = (model, owner, markers) => {
            // Log for debugging
            // console.log(`[MARKERS DEBUG] Setting ${markers.length} markers with owner: "${owner}" on ${model.uri.toString()}`);

            // Only filter out known problematic marker codes like 2347
            const filteredMarkers = markers.filter(marker => {
                if (marker.code === '2347' || (typeof marker.code === 'number' && marker.code === 2347)) {
                    console.log('[MARKERS DEBUG] Filtered out marker with code 2347');
                    return false;
                }
                return true;
            });

            // Call the original function with filtered markers
            return originalSetModelMarkers.call(this.monaco.editor, model, owner, filteredMarkers);
        };

        // Add to disposables to restore the original function when needed
        this.state.disposables.push({
            dispose: () => {
                this.monaco.editor.setModelMarkers = originalSetModelMarkers;
            }
        });

        console.log('[DEBUG] Global marker filter registered');
    }

    setServices(languageService: LanguageService, compilerService: CompilerService) {
        this.state.languageService = languageService;
        this.state.compilerService = compilerService;
    }

    public addCustomStyles() {
        // If we already have a style element with our fixed ID, no need to recreate it
        const FIXED_STYLE_ID = 'monaco-custom-styles';

        let styleElement = document.getElementById(FIXED_STYLE_ID) as HTMLStyleElement;

        // Only create new style element if it doesn't exist
        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = FIXED_STYLE_ID;
            styleElement.textContent = `
            .monaco-editor {
                position: relative !important;
                overflow: visible !important;
            }
            
            .monaco-hover-container {
                position: fixed !important;
                top: auto !important;
                left: auto !important;
                transform: none !important;
                z-index: 100000 !important;
                background-color: #FDF6E3 !important;
                border: 1px solid #93A1A1 !important;
                border-radius: 4px !important;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
                padding: 8px !important;
                max-width: 500px !important;
                pointer-events: auto !important;
            }
            
            .monaco-editor-background,
            .monaco-editor .margin {
                background-color: #FDF6E3 !important;
            }
            
            .monaco-editor .margin {
                background-color: #EEE8D5 !important;
                border-right: 10px solid #FDF6E3 !important;
            }
        `;

            document.head.appendChild(styleElement);
            this.styleMap.set(FIXED_STYLE_ID, styleElement);

            // Add to disposables only if we created a new element
            this.state.disposables.push({
                dispose: () => {
                    if (document.head.contains(styleElement)) {
                        document.head.removeChild(styleElement);
                    }
                    this.styleMap.delete(FIXED_STYLE_ID);
                }
            });
        }
    }

    private removeCustomStyles() {
        // This should only be called during complete disposal
        this.styleMap.forEach((element) => {
            if (document.head.contains(element)) {
                document.head.removeChild(element);
            }
        });
        this.styleMap.clear();
    }

    disposeCurrentModel(): void {
        if (this.state.model) {
            this.state.model.dispose();
            this.state.model = null;
        }
    }

    dispose(): void {
        // Clear styles
        this.removeCustomStyles();

        // Clear model
        this.disposeCurrentModel();

        // Clear all disposables
        this.state.disposables.forEach(disposable => disposable.dispose());
        this.state.disposables = [];

        // Reset state
        this.state = {
            model: null,
            languageService: null,
            compilerService: null,
            disposables: []
        };

        this.initialized = false;
    }
}

export default EditorModelManager;